import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Workbench pane state — shared by every screen that renders <WorkbenchShell>.
 *
 * Mounted once in AppContent (outside <Routes>) so which panes are open, which
 * line is selected, and what the cost trace is scoped to all survive
 * navigation. Nothing here knows about estimating or cost math; it is pure
 * chrome state.
 */

const INSPECTOR_STORAGE_KEY = "costpilot.workbench.inspector";
const RAIL_STORAGE_KEY = "costpilot.workbench.rail";

const TRACE_SCOPES = ["line", "job"];

/** localStorage can be blocked (private mode, sandboxed frames) — never fatal. */
function readPersistedFlag(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === "true") return true;
    if (raw === "false") return false;
  } catch (error) {
    /* storage unavailable — fall through to the default */
  }
  return fallback;
}

function writePersistedFlag(key, value) {
  try {
    window.localStorage.setItem(key, value ? "true" : "false");
  } catch (error) {
    /* storage unavailable — state still works for this session */
  }
}

const WorkbenchContext = createContext(null);

/**
 * The actual state. Extracted so `useWorkbench()` can fall back to a private
 * instance when a component is rendered outside the provider (isolated tests,
 * a component mounted standalone) instead of throwing.
 */
function useWorkbenchState() {
  const [selectedLineIndex, setSelectedLineIndexState] = useState(0);
  /**
   * The last calculation the backend returned.
   *
   * The estimate route host owns the live `results` prop, but a route change
   * can unmount and remount it — and when it does, the priced view (rail
   * amounts, status strip, rate-card panel) must come back with it rather than
   * waiting for the estimator to press Calculate again. Kept here, above
   * <Routes>, so it outlives any single screen. Nothing reads it as a
   * substitute for a fresh run; it is a survivable copy, not a second
   * authority, and it is NEVER recalculated on mount.
   */
  const [lastResults, setLastResultsState] = useState(null);
  const [traceScope, setTraceScopeState] = useState("line");
  const [inspectorOpen, setInspectorOpenState] = useState(() =>
    readPersistedFlag(INSPECTOR_STORAGE_KEY, false),
  );
  const [railCollapsed, setRailCollapsedState] = useState(() =>
    readPersistedFlag(RAIL_STORAGE_KEY, false),
  );
  // How many <WorkbenchShell>s are mounted. The app Layout uses this to know
  // whether the current route supplies its own chrome or needs the fallback bar.
  const [shellCount, setShellCount] = useState(0);

  const setSelectedLineIndex = useCallback((next) => {
    setSelectedLineIndexState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric >= 0 ? Math.trunc(numeric) : 0;
    });
  }, []);

  const setTraceScope = useCallback((next) => {
    setTraceScopeState((prev) => {
      const value = typeof next === "function" ? next(prev) : next;
      return TRACE_SCOPES.includes(value) ? value : prev;
    });
  }, []);

  const setInspectorOpen = useCallback((next) => {
    setInspectorOpenState((prev) => {
      const value = Boolean(typeof next === "function" ? next(prev) : next);
      writePersistedFlag(INSPECTOR_STORAGE_KEY, value);
      return value;
    });
  }, []);

  const toggleInspector = useCallback(() => {
    setInspectorOpen((prev) => !prev);
  }, [setInspectorOpen]);

  const setRailCollapsed = useCallback((next) => {
    setRailCollapsedState((prev) => {
      const value = Boolean(typeof next === "function" ? next(prev) : next);
      writePersistedFlag(RAIL_STORAGE_KEY, value);
      return value;
    });
  }, []);

  /** Remember a priced view. `null` clears it (new job, reset). */
  const setLastResults = useCallback((next) => {
    setLastResultsState(next || null);
  }, []);

  /** Called by WorkbenchShell on mount; returns its own unregister. */
  const registerShell = useCallback(() => {
    setShellCount((count) => count + 1);
    return () => setShellCount((count) => Math.max(0, count - 1));
  }, []);

  return useMemo(
    () => ({
      selectedLineIndex,
      setSelectedLineIndex,
      lastResults,
      setLastResults,
      traceScope,
      setTraceScope,
      inspectorOpen,
      setInspectorOpen,
      toggleInspector,
      railCollapsed,
      setRailCollapsed,
      shellCount,
      registerShell,
    }),
    [
      selectedLineIndex,
      setSelectedLineIndex,
      lastResults,
      setLastResults,
      traceScope,
      setTraceScope,
      inspectorOpen,
      setInspectorOpen,
      toggleInspector,
      railCollapsed,
      setRailCollapsed,
      shellCount,
      registerShell,
    ],
  );
}

export function WorkbenchProvider({ children }) {
  const value = useWorkbenchState();
  return (
    <WorkbenchContext.Provider value={value}>
      {children}
    </WorkbenchContext.Provider>
  );
}

export function useWorkbench() {
  const shared = useContext(WorkbenchContext);
  // Hooks must run unconditionally, so the fallback state is always created;
  // it is only *used* when there is no provider above this component.
  const standalone = useWorkbenchState();
  return shared || standalone;
}

export { WorkbenchContext, INSPECTOR_STORAGE_KEY, RAIL_STORAGE_KEY };
export default WorkbenchContext;
